import { useCallback, useEffect, useRef, useState } from 'react'
import { Show, UserButton, useAuth } from '@clerk/react'
import { Link } from 'react-router-dom'
import { getActivePod } from '@/lib/pod'
import StaffSignIn from '@/components/StaffSignIn'
import { isOwnerClaim } from '@/lib/staff-role'
import { fetchStoredDocuments, fetchDocumentFile, type StoredDocument } from '@/lib/api'
import { saveBlob } from '@/lib/save-file'
import {
  addRenewal,
  dayAfter,
  dueWords,
  fetchRenewals,
  groupRenewals,
  numberProblem,
  removeRenewal,
  renewDateProblem,
  renewRenewal,
  RenewalsError,
  type Renewal,
  type RenewalsList,
} from '@/lib/renewals'

/**
 * THE KELVO OWNER'S RENEWALS PAGE — /renewals on a staff build (batch 2y;
 * the Renewed box opens at the day after the current date, batch 2z, G50).
 *
 * What the firm must renew, and by when: a RAMS review, the insurance, a
 * certificate, a lad's site card. Overdue first, then the next 30 days, then
 * the rest. A REMINDER LIST HE KEEPS: the page says so, because keeping the
 * firm compliant stays with the client.
 *
 * A CARD IS A NAME, A CARD TYPE AND A DATE. No number field exists, and a
 * number typed into either box is refused here as he types and again by the
 * server and the database (workers/renewals.ts, db/schema.sql).
 *
 * OWNER ONLY, ENFORCED BY THE WORKER. This page hides itself from anyone whose
 * session does not say owner, as a courtesy; the owner gate refuses every
 * /api/admin/* call from anyone else regardless.
 */
const INPUT =
  'mt-1 w-full rounded-lg border border-border-subtle bg-surface-raised px-3 py-2 text-sm text-text-primary focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2 disabled:opacity-50'
const PRIMARY =
  'rounded-lg bg-brand-primary px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2'
const QUIET =
  'rounded-lg border border-border-subtle px-3 py-1.5 text-sm font-medium text-text-primary transition-colors hover:border-brand-accent disabled:opacity-50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2'
const DANGER =
  'rounded-lg border border-border-subtle px-3 py-1.5 text-sm font-medium text-text-muted transition-colors hover:text-text-danger disabled:opacity-50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2'

export default function OwnerRenewals() {
  const pod = getActivePod()
  const { isLoaded, sessionClaims } = useAuth()

  useEffect(() => {
    const previous = document.title
    document.title = `${pod.brandName} — renewals`
    return () => {
      document.title = previous
    }
  }, [pod.brandName])

  if (!isLoaded) {
    return (
      <div className="flex min-h-[100svh] items-center justify-center bg-surface-base">
        <p className="text-sm text-text-muted" role="status">
          Loading…
        </p>
      </div>
    )
  }

  return (
    <>
      <Show when="signed-out">
        <StaffSignIn returnTo="/renewals" />
      </Show>

      <Show when="signed-in">
        <div className="min-h-[100svh] bg-surface-base">
          <header className="border-b border-border-subtle bg-surface-raised">
            <div className="mx-auto flex max-w-3xl items-center justify-between gap-4 px-4 py-3">
              <div className="min-w-0">
                <p className="font-display font-semibold leading-tight tracking-tight truncate">{pod.brandName}</p>
                <p className="text-xs text-text-muted leading-tight">Renewals</p>
              </div>
              <div className="flex items-center gap-3">
                <Link
                  to="/"
                  className="rounded-lg border border-border-subtle px-3 py-1.5 text-sm font-medium text-text-primary transition-colors hover:border-brand-accent focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2"
                >
                  Back to {pod.assistantName}
                </Link>
                <UserButton />
              </div>
            </div>
          </header>

          <main className="mx-auto max-w-3xl px-4 py-8">
            {isOwnerClaim(sessionClaims) ? (
              <RenewalsBody />
            ) : (
              <section className="rounded-card border border-border-subtle bg-surface-raised p-6">
                <h1 className="font-display text-lg font-semibold tracking-tight">This page is for the owner</h1>
                <p className="mt-2 text-sm text-text-muted">
                  Renewals are kept by the owner of {pod.brandName}. If you think you should have access, speak to
                  the office.
                </p>
              </section>
            )}
          </main>
        </div>
      </Show>
    </>
  )
}

function RenewalsBody() {
  const { getToken } = useAuth()
  const getTokenRef = useRef(getToken)
  getTokenRef.current = getToken
  const [list, setList] = useState<RenewalsList | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setList(await fetchRenewals(await getTokenRef.current()))
      setError(null)
    } catch (e) {
      setError(e instanceof RenewalsError ? e.message : 'Could not load renewals.')
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  return (
    <section>
      <h1 className="font-display text-2xl font-semibold tracking-tight">Renewals</h1>
      <p className="mt-1 max-w-prose text-sm text-text-muted">
        What needs renewing, and by when: RAMS reviews, insurance, certificates, people’s cards. This is a
        reminder list you keep. Kelvo doesn’t check anything, and keeping the firm compliant stays with you.
      </p>

      <AddRenewal onAdded={load} />

      {error && (
        <p className="mt-6 text-sm text-text-danger" role="alert">
          {error}
        </p>
      )}
      {list === null && !error && (
        <p className="mt-6 text-sm text-text-muted" role="status">
          Loading…
        </p>
      )}
      {list !== null && list.renewals.length === 0 && (
        <p className="mt-6 text-sm text-text-muted">Nothing on the list yet. Add the first one above.</p>
      )}
      {list !== null &&
        groupRenewals(list.renewals).map((group) => (
          <div key={group.band} className="mt-8">
            <h2
              className={
                'text-sm font-semibold ' + (group.band === 'overdue' ? 'text-text-danger' : 'text-text-primary')
              }
            >
              {group.heading}
            </h2>
            <ul className="mt-2 stack-y-3">
              {group.items.map((r) => (
                <RenewalRow key={r.id} renewal={r} onChanged={load} />
              ))}
            </ul>
          </div>
        ))}
    </section>
  )
}

function AddRenewal({ onAdded }: { onAdded: () => Promise<void> }) {
  const { getToken } = useAuth()
  const getTokenRef = useRef(getToken)
  getTokenRef.current = getToken
  const [title, setTitle] = useState('')
  const [holder, setHolder] = useState('')
  const [dueOn, setDueOn] = useState('')
  const [documentId, setDocumentId] = useState('')
  const [documents, setDocuments] = useState<StoredDocument[]>([])
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    void (async () => {
      try {
        const docs = await fetchStoredDocuments(await getTokenRef.current())
        if (live) setDocuments(docs)
      } catch {
        // The file link is optional: without the list, the form still works.
      }
    })()
    return () => {
      live = false
    }
  }, [])

  const problem = numberProblem(title, holder)
  const ready = title.trim().length > 0 && dueOn.length > 0 && problem === null

  async function submit() {
    if (!ready || busy) return
    setBusy(true)
    setErr(null)
    try {
      await addRenewal(await getTokenRef.current(), {
        title: title.trim(),
        holder: holder.trim() || undefined,
        documentId: documentId || undefined,
        dueOn,
      })
      setTitle('')
      setHolder('')
      setDueOn('')
      setDocumentId('')
      await onAdded()
    } catch (e) {
      setErr(e instanceof RenewalsError ? e.message : 'Could not add it. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="mt-6 rounded-card border border-border-subtle bg-surface-raised p-4">
      <h2 className="text-sm font-semibold text-text-primary">Add a renewal</h2>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <label className="block text-sm text-text-primary">
          What needs renewing
          <input
            className={INPUT}
            value={title}
            maxLength={120}
            placeholder="e.g. Public liability insurance, CSCS card"
            onChange={(e) => setTitle(e.target.value)}
            disabled={busy}
          />
        </label>
        <label className="block text-sm text-text-primary">
          Whose is it? <span className="text-text-muted">(only for a card or similar)</span>
          <input
            className={INPUT}
            value={holder}
            maxLength={80}
            placeholder="Their name"
            onChange={(e) => setHolder(e.target.value)}
            disabled={busy}
          />
        </label>
        <label className="block text-sm text-text-primary">
          Renew by
          <input className={INPUT} type="date" value={dueOn} onChange={(e) => setDueOn(e.target.value)} disabled={busy} />
        </label>
        <label className="block text-sm text-text-primary">
          Linked file <span className="text-text-muted">(optional)</span>
          <select className={INPUT} value={documentId} onChange={(e) => setDocumentId(e.target.value)} disabled={busy}>
            <option value="">No file</option>
            {documents.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        </label>
      </div>
      <p className="mt-3 text-xs text-text-muted">
        Don’t type card, policy or certificate numbers. Kelvo only needs what it is and the date.
      </p>
      {problem && <p className="mt-2 text-sm text-text-danger">{problem}</p>}
      {err && (
        <p className="mt-2 text-sm text-text-danger" role="alert">
          {err}
        </p>
      )}
      <div className="mt-3">
        <button type="button" className={PRIMARY} onClick={() => void submit()} disabled={!ready || busy}>
          {busy ? 'Adding…' : 'Add'}
        </button>
      </div>
    </div>
  )
}

function RenewalRow({ renewal, onChanged }: { renewal: Renewal; onChanged: () => Promise<void> }) {
  const { getToken } = useAuth()
  const getTokenRef = useRef(getToken)
  getTokenRef.current = getToken
  const [mode, setMode] = useState<'idle' | 'renew' | 'remove'>('idle')
  const [newDate, setNewDate] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  // G50: the earliest date a renewal can move to is the day after the current
  // one. The box opens there, and nothing earlier can be saved.
  const earliest = dayAfter(renewal.dueOn)
  const dateProblem = renewDateProblem(newDate, renewal.dueOn)

  function startRenew() {
    setErr(null)
    setNewDate(earliest)
    setMode('renew')
  }

  async function act(fn: (token: string | null) => Promise<void>) {
    setBusy(true)
    setErr(null)
    try {
      await fn(await getTokenRef.current())
      setMode('idle')
      await onChanged()
    } catch (e) {
      setErr(e instanceof RenewalsError ? e.message : 'Something went wrong. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  async function openFile(id: string, name: string) {
    setErr(null)
    try {
      saveBlob(await fetchDocumentFile(await getTokenRef.current(), id, 'owner'), name)
    } catch {
      setErr('Could not open that file.')
    }
  }

  return (
    <li className="rounded-card border border-border-subtle bg-surface-raised p-4">
      <p className="font-medium text-text-primary">
        {renewal.title}
        {renewal.holder && <span className="font-normal text-text-muted"> · {renewal.holder}</span>}
      </p>
      <p className={'mt-1 text-sm ' + (renewal.band === 'overdue' ? 'font-medium text-text-danger' : 'text-text-muted')}>
        {dueWords(renewal)}
      </p>
      {renewal.document && (
        <button
          type="button"
          className="mt-2 text-sm text-text-primary underline underline-offset-2"
          onClick={() => void openFile(renewal.document!.id, renewal.document!.name)}
        >
          Open {renewal.document.name}
        </button>
      )}

      {mode === 'idle' && (
        <div className="mt-3 flex flex-wrap gap-2">
          <button type="button" className={QUIET} onClick={startRenew}>
            Renewed
          </button>
          <button type="button" className={DANGER} onClick={() => setMode('remove')}>
            Remove
          </button>
        </div>
      )}

      {mode === 'renew' && (
        <div className="mt-3">
          <label className="block text-sm text-text-primary">
            New renew-by date
            <input
              className={INPUT}
              type="date"
              value={newDate}
              min={earliest}
              onChange={(e) => setNewDate(e.target.value)}
              disabled={busy}
            />
          </label>
          {dateProblem && <p className="mt-2 text-sm text-text-danger">{dateProblem}</p>}
          <div className="mt-2 flex flex-wrap gap-2">
            <button
              type="button"
              className={PRIMARY}
              disabled={busy || !newDate || dateProblem !== null}
              onClick={() => void act((t) => renewRenewal(t, renewal.id, newDate))}
            >
              {busy ? 'Saving…' : 'Save new date'}
            </button>
            <button type="button" className={QUIET} disabled={busy} onClick={() => setMode('idle')}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {mode === 'remove' && (
        <div className="mt-3">
          <p className="text-sm text-text-primary">Remove this from the list? It is deleted for good.</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <button
              type="button"
              className={DANGER}
              disabled={busy}
              onClick={() => void act((t) => removeRenewal(t, renewal.id))}
            >
              {busy ? 'Removing…' : 'Yes, remove it'}
            </button>
            <button type="button" className={QUIET} disabled={busy} onClick={() => setMode('idle')}>
              Keep it
            </button>
          </div>
        </div>
      )}

      {err && (
        <p className="mt-2 text-sm text-text-danger" role="alert">
          {err}
        </p>
      )}
    </li>
  )
}
